<template>
  <header
    id="HeaderTheme"
    :class="[
      'mahai-header',
      { sticky: isSticky },
      { active: stateStoreInstance.open },
    ]"
  >
    <div class="mahai-header-inner">
      <div class="mahai-header-left">
        <button
          type="button"
          :class="['mahai-burger', { active: stateStoreInstance.open }]"
          @click="stateStoreInstance.onChange"
          aria-label="Menüyü aç/kapat"
        >
          <span class="material-symbols-outlined">menu</span>
        </button>
      </div>

      <div class="mahai-header-center">
        <span class="mahai-header-title">Yüksek Mahkeme Bilgi İşlem Merkezi - Yapay Zeka Platformu</span>
      </div>

      <div class="mahai-header-right" v-if="isLawAssistantRoute">
        <div class="mahai-header-actions">
          <button
            type="button"
            class="mahai-action-btn precedent-btn"
            @click="handlePrecedentSearch"
            aria-label="Emsal karar ara"
            title="Emsal Ara"
          >
            <span class="material-symbols-outlined">search</span>
          </button>

          <button
            type="button"
            class="mahai-action-btn"
            @click="handleShare"
            aria-label="Sohbeti paylaş"
            title="Paylaş"
          >
            <span class="material-symbols-outlined">ios_share</span>
          </button>

          <button
            type="button"
            class="mahai-action-btn"
            @click="handleClear"
            aria-label="Sohbeti temizle"
            title="Temizle"
          >
            <span class="material-symbols-outlined">delete_sweep</span>
          </button>

          <button
            type="button"
            class="mahai-action-btn"
            @click="handleExportPdf"
            aria-label="PDF olarak indir"
            title="PDF İndir"
          >
            <span class="material-symbols-outlined">picture_as_pdf</span>
          </button>

          <button
            type="button"
            class="mahai-action-btn"
            @click="handleExportWord"
            aria-label="Word olarak indir"
            title="Word İndir"
          >
            <span class="material-symbols-outlined">description</span>
          </button>
        </div>
      </div>
    </div>
  </header>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  onMounted,
  computed,
  onBeforeUnmount,
} from "vue";
import { useRoute } from "vue-router";
import stateStore from "@/utils/store";

export default defineComponent({
  name: "TopHeader",

  setup() {
    const stateStoreInstance = stateStore;
    const isSticky = ref(false);
    const route = useRoute();

    const currentConversationId = ref<number | null>(null);
    const currentUserId = ref<number | null>(null);

    const isLawAssistantRoute = computed(() => {
      return (
        route.name === "LawAssistantPage" ||
        (!!route.path && route.path.startsWith("/apps/decisions-ai"))
      );
    });

    const onScroll = () => {
      const scrollPos = window.scrollY || window.pageYOffset;
      isSticky.value = scrollPos >= 100;
    };

    const onConversationChanged = (event: Event) => {
      const custom: any = event;
      currentConversationId.value =
        custom?.detail && typeof custom.detail.id === "number"
          ? custom.detail.id
          : null;
    };

    const handleShare = () => {
      window.dispatchEvent(
        new CustomEvent("decisions-ai-share-conversation", {
          detail: { conversationId: currentConversationId.value },
        })
      );
    };

    const handleClear = () => {
      window.dispatchEvent(
        new CustomEvent("decisions-ai-clear-chat", {
          detail: { conversationId: currentConversationId.value },
        })
      );
    };

    const handleExportPdf = () => {
      window.dispatchEvent(
        new CustomEvent("decisions-ai-export-pdf", {
          detail: { conversationId: currentConversationId.value },
        })
      );
    };

    const handleExportWord = () => {
      window.dispatchEvent(
        new CustomEvent("decisions-ai-export-word", {
          detail: { conversationId: currentConversationId.value },
        })
      );
    };

    const handlePrecedentSearch = () => {
      window.dispatchEvent(
        new CustomEvent("decisions-ai-precedent-search", {
          detail: {},
        })
      );
    };

    onMounted(() => {
      window.addEventListener("scroll", onScroll);
      window.addEventListener(
        "decisions-ai-conversation-changed",
        onConversationChanged
      );

      const raw = localStorage.getItem('auth_user');
      if (raw) {
        const user = JSON.parse(raw);
        currentUserId.value = Number(user?.id) || null;
      }
    });

    onBeforeUnmount(() => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener(
        "decisions-ai-conversation-changed",
        onConversationChanged
      );
    });

    return {
      stateStoreInstance,
      isSticky,
      isLawAssistantRoute,
      currentConversationId,
      currentUserId,
      handleShare,
      handleClear,
      handleExportPdf,
      handleExportWord,
      handlePrecedentSearch,
    };
  },
});
</script>

<style lang="scss" scoped>
.mahai-header {
  position: fixed;
  top: 0;
  left: 260px;
  right: 0;
  z-index: 5;
  padding: 0 24px;
  height: 56px;
  display: flex;
  align-items: center;
  background: var(--paper);
  border-bottom: 1px solid var(--rule);
  transition: left 0.3s ease, box-shadow 0.2s ease;

  &.sticky {
    box-shadow: 0 1px 4px rgba(15, 23, 42, 0.06);
  }

  &.active {
    left: 60px;
  }
}

.mahai-header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  position: relative;
}

.mahai-header-left {
  display: flex;
  align-items: center;
}

.mahai-burger {
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
  color: #475569;

  .material-symbols-outlined {
    font-size: 22px;
  }

  &:hover {
    background: #e2e8f0;
    color: #0f172a;
  }
}

.mahai-header-center {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  pointer-events: none;
}

.mahai-header-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
  font-family: var(--font-serif);
  letter-spacing: 0.01em;
  letter-spacing: 0.01em;
  white-space: nowrap;
}

.mahai-header-right {
  display: flex;
  align-items: center;
}

.mahai-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.mahai-action-btn {
  background: transparent;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  width: 34px;
  height: 34px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #64748b;
  transition: all 0.15s ease;

  .material-symbols-outlined {
    font-size: 18px;
  }

  &:hover {
    background: #e2e8f0;
    border-color: #94a3b8;
    color: #334155;
  }

  &:active {
    background: #cbd5e1;
  }
}

/* Tablet */
@media only screen and (max-width: 991px) {
  .mahai-header {
    position: relative;
    left: 0 !important;
    padding: 0 16px;
    height: 52px;
    margin-bottom: 0;

    &.active {
      left: 0 !important;
    }

    &.sticky {
      position: fixed;
      left: 0 !important;
      right: 0;
    }
  }
}

/* Mobile */
@media only screen and (max-width: 767px) {
  .mahai-header {
    padding: 0 12px;
    height: 48px;
  }

  .mahai-header-center {
    display: none;
  }

  .mahai-action-btn {
    width: 30px;
    height: 30px;

    .material-symbols-outlined {
      font-size: 16px;
    }
  }
}
</style>
